import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ShiftResponse } from '@/api/handlers/resources/shifts/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { getUser } from '@/utils/authSession';
import { ShiftQueryKeys } from './shiftQueryKeys';

/**
 * How often the terminal re-reads its shift while it sits idle (SEL-27). A
 * seller's shift is theirs, not the terminal's: it can be opened on another
 * sale point or closed by an admin, and nothing tells this one.
 */
export const SHIFT_REFRESH_INTERVAL_MS = 30_000;

/**
 * The signed-in seller's own open shift (server source of truth). Resolves to a
 * `null` response when they have none (the service maps the 204 to `null`).
 * Keyed by the seller, so a seller signing in on this terminal never sees the
 * previous seller's cached shift.
 * `staleTime` is 0 so the gate reflects an open/close the moment it lands, and
 * the query re-reads on window focus and every `SHIFT_REFRESH_INTERVAL_MS` so a
 * change made elsewhere reaches this terminal without an action to trigger it.
 */
export const useCurrentShiftQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<ShiftResponse | null>>({
        queryKey: ShiftQueryKeys.current(getUser()?.id ?? null),
        queryFn: () => apiClient.shifts.getCurrent(),
        enabled,
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchInterval: SHIFT_REFRESH_INTERVAL_MS,
    });
};
